import * as d3 from 'd3';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  appendDonutCentreText,
  appendDonutGraph,
  appendEmptyDonutGraph,
} from 'src/utils';
import { useTheme } from 'styled-components';
import { DonutContainer } from './Donut.styles';

interface DonutProps {
  selectedSegment?: string;
  width?: number;
  centerLabel: string;
  data: { [index: string]: number } | undefined;
  colors?: string[];
  onClick?: (d: d3.PieArcDatum<[string, number]>) => void;
}

const OPACITYVALUE = 0.4;
const EXTRASPACEFORHIGHLIGHT = 30;
const OUTERRADIUSADJUSTMENT = 0.6;
const INNERRADIUSADJUSTMENT = 1.1;
const OUTERRADIUSHIGHLIGHTADJUSTMENT = 0.85;
const INNERRADIUSHIGHLIGHTADJUSTMENT = 1.1;
const WIDTHADJUSTMENT = 1.23;

export const Donut = ({
  centerLabel,
  data,
  colors,
  width = 262,
  selectedSegment,
  onClick,
}: DonutProps) => {
  const donutRef = useRef(null);
  const selectedDonutRef = useRef(null);
  const theme = useTheme();

  const innerRadius = width / 2;
  const innerRadiusAdjusted = innerRadius * INNERRADIUSADJUSTMENT;
  const innerRadiusForHighlight = innerRadius * INNERRADIUSHIGHLIGHTADJUSTMENT;
  const outerRadius = innerRadius * OUTERRADIUSADJUSTMENT;
  const outerRadiusForHighlight = outerRadius * OUTERRADIUSHIGHLIGHTADJUSTMENT;
  const size = width + EXTRASPACEFORHIGHLIGHT;

  const pie = useMemo(() => d3.pie<[string, number]>().value((d) => d[1]), []);

  const pieData = useMemo(() => {
    const filteredData = Object.entries(data || {}).filter(
      ([_key, value]) => value !== 0,
    );
    return pie(filteredData);
  }, [data, pie]);

  const [highlightedSegment, setHighlightedSegment] = useState<
    string | undefined
  >(selectedSegment);

  useEffect(() => {
    if (selectedSegment) {
      setHighlightedSegment(selectedSegment);
      setHoverSegment(undefined);
    }
  }, [pieData, selectedSegment]);

  useEffect(() => {
    if (highlightedSegment === undefined) {
      setHighlightedSegment(pieData.find((d) => d.index === 0)?.data[0]);
    }
  }, [pieData, highlightedSegment]);

  const [hoverSegment, setHoverSegment] = useState<string | undefined>(
    undefined,
  );

  const valueToShow = useMemo(
    () =>
      data && hoverSegment
        ? data[hoverSegment]
        : data && highlightedSegment
          ? data[highlightedSegment]
          : undefined,
    [data, hoverSegment, highlightedSegment],
  );

  useLayoutEffect(() => {
    const donutGraph = d3.select(donutRef.current);
    const selectedDonutGraph = d3.select(selectedDonutRef.current);
    donutGraph.selectAll('*').remove();
    selectedDonutGraph.selectAll('*').remove();

    donutGraph.attr(
      'transform',
      `translate(${innerRadiusAdjusted}, ${innerRadiusAdjusted})`,
    );

    selectedDonutGraph.attr(
      'transform',
      `translate(${innerRadiusAdjusted}, ${innerRadiusAdjusted})`,
    );

    const datum = d3
      .arc<d3.PieArcDatum<[string, number]>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);

    const datumForSelectedDonutGraph = d3
      .arc<d3.PieArcDatum<[string, number]>>()
      .innerRadius(innerRadiusForHighlight)
      .outerRadius(outerRadiusForHighlight);

    const allValuesAreZero = Object.values(data ?? {}).every(
      (value) => value === 0,
    );

    if (!data || Object.keys(data).length === 0 || allValuesAreZero) {
      appendEmptyDonutGraph({
        donutGraph,
        pieData: pie(Object.entries({ noDataPoint: 1 })),
        datum,
        fillColor: theme.atlas.semantic.color.outline.disabled,
      });

      appendDonutCentreText({
        donutGraph,
        label: centerLabel,
        value: '\u2013',
        theme,
      });
    } else {
      appendDonutGraph({
        donutGraph,
        pieData,
        colors,
        datum,
        setHighlightedSegment,
        setHoverSegment,
        onClick,
        opacityFunction: (segment) =>
          highlightedSegment
            ? segment === highlightedSegment || hoverSegment === segment
              ? 1
              : OPACITYVALUE
            : 1,
      });

      appendDonutGraph({
        donutGraph: selectedDonutGraph,
        pieData,
        colors,
        datum: datumForSelectedDonutGraph,
        pointerEvents: 'none',
        setHighlightedSegment,
        opacityFunction: (segment) =>
          segment === hoverSegment ? OPACITYVALUE : 0,
      });

      if (valueToShow !== undefined) {
        appendDonutCentreText({
          donutGraph,
          label: centerLabel,
          value: valueToShow,
          theme,
        });
      }
    }
  }, [
    pie,
    innerRadius,
    outerRadius,
    pieData,
    highlightedSegment,
    hoverSegment,
    valueToShow,
    innerRadiusAdjusted,
    innerRadiusForHighlight,
    outerRadiusForHighlight,
    data,
    centerLabel,
    colors,
    onClick,
    theme,
  ]);

  return (
    <DonutContainer size={width * WIDTHADJUSTMENT}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={`100%`}
        height={`100%`}
        data-testid="donut-chart"
      >
        <g ref={donutRef} />
        <g ref={selectedDonutRef} />
      </svg>
    </DonutContainer>
  );
};
